import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import DevRoleSwitcher from './components/DevRoleSwitcher';
import PrinterAlertToasts from './components/PrinterAlertToasts';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { isFlagOn } from './utils/featureFlags';

// Code-split all page components for faster initial load. Use lazyWithRetry
// so deploys don't trip stale-tab users with "Failed to fetch dynamically
// imported module" errors — we reload once on chunk fetch failure.
const Login = lazyWithRetry(() => import('./pages/Login'));
const InviteAccept = lazyWithRetry(() => import('./pages/InviteAccept'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Users = lazyWithRetry(() => import('./pages/Users'));
const Customers = lazyWithRetry(() => import('./pages/Customers'));
const Sessions = lazyWithRetry(() => import('./pages/Sessions'));
const Filters = lazyWithRetry(() => import('./pages/Filters'));
const Booths = lazyWithRetry(() => import('./pages/Booths'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const SetupWizard = lazyWithRetry(() => import('./pages/SetupWizard'));
const Payments = lazyWithRetry(() => import('./pages/Payments'));
const CreditPackages = lazyWithRetry(() => import('./pages/CreditPackages'));
const Organizations = lazyWithRetry(() => import('./pages/Organizations'));
const Themes = lazyWithRetry(() => import('./pages/Themes'));
const VersionManagement = lazyWithRetry(() => import('./pages/VersionManagement'));
const PayoutBreakdown = lazyWithRetry(() => import('./pages/PayoutBreakdown'));
const SalesDashboard = lazyWithRetry(() => import('./pages/SalesDashboard'));
// SalesPipeline deprecated — redirects to /crm. Keep the lazy import removed
// so the chunk isn't built.
const ScreenThemes = lazyWithRetry(() => import('./pages/ScreenThemes'));
const Reports = lazyWithRetry(() => import('./pages/Reports'));
const DiscountCodes = lazyWithRetry(() => import('./pages/DiscountCodes'));
const SocialMedia = lazyWithRetry(() => import('./pages/SocialMedia'));
const BoothInquiries = lazyWithRetry(() => import('./pages/BoothInquiries'));
const UsersAndAccess = lazyWithRetry(() => import('./pages/UsersAndAccess'));
const PrivacyRequests = lazyWithRetry(() => import('./pages/PrivacyRequests'));
const CrmDashboard = lazyWithRetry(() => import('./pages/crm/CrmDashboard'));
const CrmContacts = lazyWithRetry(() => import('./pages/crm/Contacts'));
const CrmContactDetail = lazyWithRetry(() => import('./pages/crm/ContactDetail'));
const CrmTasks = lazyWithRetry(() => import('./pages/crm/Tasks'));
const CrmAdvancedTools = lazyWithRetry(() => import('./pages/crm/AdvancedTools'));
const AffiliatesPage = lazyWithRetry(() => import('./pages/affiliates/AffiliatesPage'));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

const HomeRoute = () => {
  const { userRole } = useAuth();
  // Affiliates have no dashboard — their whole world is /affiliates.
  if (userRole === 'affiliate') return <Navigate to="/affiliates" replace />;
  return userRole === 'salesperson' ? <SalesDashboard /> : <Dashboard />;
};

// Full-screen shell for record details opened from a list. The list route
// stays mounted underneath (see OverlayAwareRoutes), so Back closes the
// detail instantly with no list refetch.
const DetailOverlay = ({ children }) => (
  <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-100">
    <div className="p-6">{children}</div>
  </div>
);

// Background-location pattern: when a list navigates to a detail route with
// state.backgroundLocation, the main <Routes> keeps rendering the list (from
// that background location — filters and pagination intact) while the detail
// renders as an overlay on top. Cold deep links carry no state and fall
// through to the normal full-page detail routes.
const OverlayAwareRoutes = ({ children }) => {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;
  return (
    <>
      <Routes location={backgroundLocation || location}>{children}</Routes>
      {backgroundLocation && (
        <Routes>
          <Route
            path="/sessions/:id"
            element={
              <ProtectedRoute>
                <DetailOverlay><Sessions /></DetailOverlay>
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/:id"
            element={
              <ProtectedRoute requireRoles={['superadmin', 'admin']}>
                <DetailOverlay><Payments /></DetailOverlay>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={null} />
        </Routes>
      )}
    </>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <OrganizationProvider>
          <DevRoleSwitcher />
          <PrinterAlertToasts />
          <Toaster position="top-right" />
          <Suspense fallback={<LoadingSpinner />}>
          <OverlayAwareRoutes>
            {/* Public Routes */}
            <Route path="/setup" element={<SetupWizard />} />
            <Route path="/login" element={<Login />} />
            {/* Staff invite acceptance — unauthenticated by design, the
                invite token is the credential. */}
            <Route path="/invite/:token" element={<InviteAccept />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <HomeRoute />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Organizations />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'admin', 'org_owner']}>
                  <Layout>
                    <Customers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Sessions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Sessions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/filters"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'admin']}>
                  <Layout>
                    <Filters />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/booths"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Booths />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'admin']}>
                  <Layout>
                    <Payments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/:id"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'admin']}>
                  <Layout>
                    <Payments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packages"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'admin']}>
                  <Layout>
                    <CreditPackages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/themes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Themes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/screen-themes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ScreenThemes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/org-managers" element={<Navigate to="/users-access" replace />} />
            <Route
              path="/payout-breakdown"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayoutBreakdown />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/discount-codes"
              element={
                <ProtectedRoute requireRoles={['superadmin']}>
                  <Layout>
                    <DiscountCodes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/social-media"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'admin', 'org_owner']}>
                  <Layout>
                    <SocialMedia />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/booth-inquiries"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BoothInquiries />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/booth-inquiries/:inquiryId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BoothInquiries />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/versions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VersionManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SalesDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/sales-team" element={<Navigate to="/users-access" replace />} />
            <Route path="/team" element={<Navigate to="/users-access" replace />} />
            <Route path="/org-owners" element={<Navigate to="/users-access" replace />} />
            {/* /sales-pipeline deprecated — use /crm instead. Redirect for any
                stale bookmarks. The old SalesPipeline.jsx + sales_leads
                collection are left in place; data is preserved, just no UI. */}
            <Route path="/sales-pipeline" element={<Navigate to="/crm" replace />} />

            {/* CRM (superadmin + salesperson). Route-level role check bounces
                anyone else to / before the chunk even loads. Defense in depth
                on top of the inline role checks each CRM page also has. */}
            <Route
              path="/crm"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'salesperson']}>
                  <Layout>
                    <CrmDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/contacts"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'salesperson']}>
                  <Layout>
                    <CrmContacts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/contacts/:id"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'salesperson']}>
                  <Layout>
                    <CrmContactDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/tasks"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'salesperson']}>
                  <Layout>
                    <CrmTasks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/tools"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'salesperson']}>
                  <Layout>
                    <CrmAdvancedTools />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Legacy standalone tool routes redirect to the new tabbed view.
                The tools now live as tabs inside /crm/tools?tab=… so bookmarks
                from the previous design still work. */}
            <Route path="/crm/tools/lead-scoring" element={<Navigate to="/crm/tools?tab=lead-scoring" replace />} />
            <Route path="/crm/tools/referrals" element={<Navigate to="/crm/tools?tab=referrals" replace />} />
            <Route path="/crm/tools/automations" element={<Navigate to="/crm/tools?tab=automations" replace />} />

            {/* Phase 3 — consolidated Users & Access screen, gated by feature flag */}
            {isFlagOn('users-access') && (
              <Route
                path="/users-access"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UsersAndAccess />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            )}

            {/* Affiliate program — tabbed page (Contacts / Pipeline /
                Documents / Info, + Roster for superadmins). All data lives
                in affiliate_* collections; demo-seeded content never touches
                real metrics. */}
            <Route
              path="/affiliates"
              element={
                <ProtectedRoute requireRoles={['superadmin', 'affiliate']}>
                  <Layout>
                    <AffiliatesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Privacy / data-deletion request queue. The page enforces
                superadmin itself; ProtectedRoute only gates sign-in. */}
            <Route
              path="/privacy-requests"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PrivacyRequests />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </OverlayAwareRoutes>
          </Suspense>
        </OrganizationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
